import { useAuthStore } from '@/lib/auth/AuthStore'
import { mixpanelEvent } from '@/lib/mixpanels/mixpanel'
import { PASSES_ORDER, SOLVOOK_WRITE_STUDIO } from '@/settings/constant'
import { ConfirmDialog } from '@bookips/solvook-ui-library'
import { Divider, styled, Typography, useTheme } from '@mui/material'
import { useQuery } from '@tanstack/react-query'
import { useRouter } from 'next/router'
import React, { useCallback, useState } from 'react'
import {
	GetMembershipResponse,
	getMemberships,
	GetPassResponseData,
	PassCategory,
} from 'src/api/membership'
import LoginDialog from 'src/store/loginDialog'
import usePassStore from 'src/store/pass'
import { Container, SubTitle } from '.'
import Icon from '../icon/Icon'
import { ArrowRight2px, Check_Thin, Sizes } from '../icon/IconList'
import { PassInfoModal } from './PassInfoModal'
import { PassItem } from './PassItem'
import { AxiosError } from 'axios'

export interface PassListProps {
	data: GetPassResponseData
	handleSubscribeWarningDialog: () => void
}

//이용권 구매 리스트
export const PassList: React.FC<PassListProps> = ({ data, handleSubscribeWarningDialog }) => {
	const theme = useTheme()
	const { push } = useRouter()

	const { selectPassItem, closePassInfoModal } = usePassStore((state) => ({
		selectPassItem: state.selectedPass,
		closePassInfoModal: state.closePassInfoModal,
	}))

	const { isLoggedIn } = useAuthStore()
	const { openLoginDialog } = LoginDialog()

	const [existSolvook, setExistSolvook] = useState(false) //이미 쏠북을 이용중이에요.

	const { data: userMembershipResponse } = useQuery<GetMembershipResponse, AxiosError>(
		['userMembership'],
		() => getMemberships({}),
		{
			onError: (error: AxiosError) => {
				console.log('onError', error)
			},
			enabled: isLoggedIn,
			retry: 3,
		},
	)

	//엑스퍼트 이용권 구매시 사용자의 이용권 확인, 쏠플, 쏠구독권인 사람은 기간권을 구매할 수 없다.
	const checkPassCondition = useCallback(async () => {
		if (!selectPassItem) return
		if (selectPassItem.category !== PassCategory.Expert) return

		if (!isLoggedIn) {
			openLoginDialog()
			closePassInfoModal()
			return
		}
		try {
			// unexpired에서 확인을 하기 때문에, 만료된 이용권은 확인하지 않는다.
			const isExistSolvookPass = userMembershipResponse?.unexpired.filter((item) => {
				if (
					item.productInfo.category.includes('solvookplus') ||
					item.productInfo.category.includes('solvookplus-subscribe') ||
					(item.productInfo.categoryTitle === '쏠북 플러스' &&
						item.productInfo.category.includes('ticket'))
				) {
					return item
				}
			})

			if (isExistSolvookPass.length) {
				closePassInfoModal()
				setExistSolvook(true)
				return
			}
		} catch (e) {
			// 현재 404 에러만 존재합니다. @TO-DO 에러 타입
			console.log('catch : ', e instanceof Error)
		}

		push(`${PASSES_ORDER}?id=${selectPassItem.id}`)
		closePassInfoModal()
	}, [selectPassItem, userMembershipResponse])

	if (data.subject === '') return <></>
	return (
		<>
			{data.products.map((group, index) => {
				const {
					categoryTitle,
					categoryTitleDescription,
					categortyTitleDescriptionUrl,
					items,
				} = group
				return (
					<Container key={index}>
						<SubTitle>{categoryTitle}</SubTitle>
						{categoryTitleDescription && categortyTitleDescriptionUrl && (
							<MoreInfoButtonBox
								href={categortyTitleDescriptionUrl}
								onClick={() => {
									// mx-154 이용권 카테고리 설명 모듈 클릭 시
									mixpanelEvent('Pass Category Description Clicked', {
										'Pass Categories': categoryTitle,
										Url: categortyTitleDescriptionUrl,
									})
								}}>
								<Icon
									icon={Check_Thin}
									color={theme.palette.secondary.main}
									size={Sizes.xmedium}
								/>
								<div>
									<Typography>{categoryTitleDescription}</Typography>
									<Icon
										icon={ArrowRight2px}
										size={Sizes.small}
										color={theme.palette.grey[500]}
									/>
								</div>
							</MoreInfoButtonBox>
						)}

						<PackageList>
							{items?.map((pass, index) => {
								return (
									<PassItem
										key={index}
										pass={pass}
										handleSubscribeWarningDialog={handleSubscribeWarningDialog}
										existSolvook={() => setExistSolvook((prev) => !prev)}
									/>
								)
							})}
						</PackageList>
						{index + 1 < data.products.length && <Divider />}
					</Container>
				)
			})}
			{/* PassItem에서 공통으로 사용하는 다이얼로그 */}
			<PassInfoModal
				ctaAction={
					selectPassItem && selectPassItem.category === PassCategory.Expert
						? () => {
								checkPassCondition()
								//mx-3 구매하기 버튼 클릭시
								mixpanelEvent('Purchase Button Clicked', {
									'Product Categories': selectPassItem.category,
									'Product ID': selectPassItem.id,
									'Product Title': selectPassItem.title,
									'Product Price': selectPassItem.price,
								})
						  }
						: null
				}
			/>

			<ConfirmDialog
				open={existSolvook}
				onClose={() => setExistSolvook(false)}
				title="이미 쏠북 플러스를 이용 중이에요."
				content={'쏠북 플러스 기간동안 엑스퍼트는 추가 결제없이 사용할 수 있어요.'}
				onOk={() => {
					// mx-9
					mixpanelEvent('Popup Button Clicked', {
						'Popup Type': 'Already Subscribed',
						'Popup Title': '이미 쏠북 플러스를 이용 중이에요',
						'Button Name': '엑스퍼트 사용하기',
						'Button URL': SOLVOOK_WRITE_STUDIO,
					})
					// handleSubscribeWarningDialog()
					push(SOLVOOK_WRITE_STUDIO)
				}}
				onCancel={() => {
					// mx-9
					mixpanelEvent('Popup Button Clicked', {
						'Popup Type': 'Already Subscribed',
						'Popup Title': '이미 쏠북 플러스를 이용 중이에요',
						'Button Name': '확인',
						'Button URL': null,
					})
					setExistSolvook(false)
				}}
				cancelText="확인"
				okText="엑스퍼트 사용하기"
				sx={{
					button: {
						width: '100%',
					},
				}}
			/>
		</>
	)
}

const PackageList = styled('ul')`
	display: flex;
	flex-direction: column;
	gap: 12px;

	margin-bottom: 40px;
`

const MoreInfoButtonBox = styled('a')(({ theme }) => ({
	display: 'flex',
	backgroundColor: theme.palette.blue[50],
	gap: '8px',

	padding: '12px',
	borderRadius: '6px',
	marginBottom: '12px',
	div: {
		width: '100%',
		display: 'flex',
		gap: '12px',
		alignItems: 'center',
		justifyContent: 'space-between',
	},

	'&:hover': {
		backgroundColor: theme.palette.blue[100],
	},
}))
